/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import { Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import { useTheme } from "@mui/system";
import clsx from "clsx";

import { classes as defaultClasses, YearAndMonthSelect } from "./SelectMonth";

type SelectYearProps = {
  store?: {
    [key: string]: number | string | boolean | undefined;
    is_data_processed?: boolean;
  };
  name?: string;
  classes?: Record<string, string>;
  classesOverride?: Record<string, string>;
  disabled?: boolean;
  onChange?: ((value: number | string, name: string) => void) | null;
  options?: (number | string)[];
  color?: string;
  menuItemClasses?: {
    root?: string | null;
    selected?: string | null;
  };
  [key: string]: unknown; // for restProps
};

const YEARS_AMOUNT = 8;
const MIN_YEAR_AMOUNT = 13;

// eslint-disable-next-line react-refresh/only-export-components
function SelectYear({
  store = {},
  name = "year",
  classes = {},
  classesOverride = {},
  disabled = false,
  onChange = null,
  options: userOptions = [],
  color = "primary",
  menuItemClasses, // intentionally unused, filtered from restProps
  ...restProps
}: SelectYearProps) {
  const theme = useTheme();
  const yearClasses = { ...defaultClasses, ...classes, ...classesOverride };
  const defaultYear = store[name] || 2019;
  const yearOptions = useMemo(() => {
    if (Array.isArray(userOptions) && userOptions.length === 0) {
      const years = [];
      for (let i = 0; i < YEARS_AMOUNT; i++) {
        years.push(defaultYear - i + 3);
      }
      return years;
    }
    return userOptions;
  }, [userOptions, defaultYear]);

  const getSelectedIndex = useCallback(
    () => yearOptions.indexOf(defaultYear),
    [yearOptions, defaultYear]
  );

  const [selectedIndex, setSelectedIndex] = useState(getSelectedIndex);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const toggleMenuOpen = useCallback(() => {
    setIsMenuOpen((current) => !current);
  }, []);

  const toggleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  function handleSelectItem(_: React.MouseEvent<HTMLLIElement>, index: number) {
    setSelectedIndex(index);
    const newOption = yearOptions[index];
    if (typeof onChange === "function") {
      onChange(newOption, name);
    }
    runInAction(() => {
      if (name in store) {
        store[name] = newOption;
      }
    });
    setIsMenuOpen(false);
  }

  useEffect(() => {
    setSelectedIndex(getSelectedIndex);
  }, [getSelectedIndex, yearOptions]);

  const selectedYear = store[name] || yearOptions[selectedIndex] || userOptions[0];

  return (
    <YearAndMonthSelect color={color}>
      <Button
        ref={anchorRef}
        onClick={toggleMenuOpen}
        {...restProps}
        className={clsx(defaultClasses.selectDate, classesOverride)}
        classes={yearClasses}
        endIcon={isMenuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
        disabled={disabled ?? store.is_data_processed === false}
        data-testid="select-year-button"
      >
        {selectedYear}
      </Button>
      <Popper
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            sx={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom" }}
          >
            <Paper>
              <ClickAwayListener onClickAway={toggleMenuClose}>
                <MenuList
                  data-testid="select-year-list"
                  sx={{
                    maxHeight: "400px",
                    overflowY: yearOptions?.length >= MIN_YEAR_AMOUNT && "scroll",
                  }}
                >
                  {yearOptions.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleSelectItem(event, index)}
                      classes={{ root: yearClasses.menuItem, selected: yearClasses.menuSelected }}
                      data-testid={`year-option-${option}`}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </YearAndMonthSelect>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default observer(SelectYear);
